import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Card, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type IconColor = "blue" | "orange" | "green" | "red" | "violet" | "emerald";

const iconColorClasses: Record<IconColor, string> = {
	blue: "bg-blue-100 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400",
	orange:
		"bg-orange-100 text-orange-600 dark:bg-orange-950/50 dark:text-orange-400",
	green: "bg-green-100 text-green-600 dark:bg-green-950/50 dark:text-green-400",
	red: "bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-400",
	violet:
		"bg-violet-100 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400",
	emerald:
		"bg-emerald-100 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400",
};

type DeltaDirection = "down-good" | "up-good" | "neutral";

export interface StatCardProps {
	icon: React.ReactNode;
	label: string;
	value: number | string | undefined;
	loading: boolean;
	noneWhenZero?: boolean;
	subtitle?: string;
	unit?: string;
	deltaPercent?: number;
	previousValue?: string;
	deltaDirection?: DeltaDirection;
	hideDelta?: boolean;
	color?: IconColor;
	to?: string;
}

export function StatCard({
	icon,
	label,
	value,
	loading,
	noneWhenZero,
	subtitle,
	unit,
	deltaPercent,
	previousValue,
	deltaDirection = "down-good",
	hideDelta,
	color,
	to,
}: StatCardProps) {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const displayValue =
		noneWhenZero && value === 0 ? (
			<i className="text-muted-foreground text-base font-normal not-italic">
				{t("common.none")}
			</i>
		) : (
			(value ?? "—")
		);

	const isGood =
		deltaPercent == null || deltaPercent === 0
			? null
			: deltaDirection === "neutral"
				? null
				: deltaDirection === "down-good"
					? deltaPercent < 0
					: deltaPercent > 0;

	const pillClass =
		isGood == null
			? "bg-muted text-muted-foreground"
			: isGood
				? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400"
				: "bg-red-100 text-red-700 dark:bg-red-950/50 dark:text-red-400";

	return (
		<Card
			className={cn(
				"bg-muted/30 transition-colors",
				to && "cursor-pointer hover:ring-foreground/25",
			)}
			onClick={to ? () => navigate(to) : undefined}
		>
			<CardHeader className="flex flex-col gap-2">
				<div className="flex items-center gap-2">
					<div
						className={
							color
								? `rounded-lg p-1.5 ${iconColorClasses[color]}`
								: "text-muted-foreground"
						}
						aria-hidden="true"
					>
						{icon}
					</div>
					<span className="text-muted-foreground text-xs">{label}</span>
				</div>
				{loading ? (
					<Skeleton className="h-8 w-24" aria-label="Ładowanie…" />
				) : (
					<div className="flex items-baseline gap-1.5">
						<span className="text-foreground text-2xl font-bold tabular-nums">
							{displayValue}
						</span>
						{unit && (
							<span className="text-muted-foreground text-sm">{unit}</span>
						)}
					</div>
				)}
				{hideDelta ? (
					subtitle != null && (
						<p className="text-muted-foreground text-xs">{subtitle}</p>
					)
				) : deltaPercent != null ? (
					<p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
						<span className="text-muted-foreground">
							{t("dashboard.previousPeriod")}:{" "}
							<span className="text-foreground tabular-nums">
								{previousValue ?? "—"}
							</span>
							{unit && previousValue && (
								<span className="text-muted-foreground"> {unit}</span>
							)}
						</span>
						<span
							className={cn(
								"inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-medium",
								pillClass,
							)}
						>
							{deltaPercent > 0 ? (
								<CaretUp className="size-3" aria-hidden="true" />
							) : deltaPercent < 0 ? (
								<CaretDown className="size-3" aria-hidden="true" />
							) : null}
							{Math.abs(deltaPercent).toFixed(1)}%
						</span>
					</p>
				) : subtitle != null ? (
					<p className="text-muted-foreground text-xs">{subtitle}</p>
				) : (
					<p className="text-muted-foreground text-xs italic">
						{t("dashboard.noComparison")}
					</p>
				)}
			</CardHeader>
		</Card>
	);
}
